import { Error as ErrorIcon } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';

import { brandColors } from 'colors';
import Button from 'domains/core/components/Button';

export type Props = {
    onRetry: () => void;
};

/**
 * Full-content-area error screen shown when brand profile data fails to load.
 * Intentionally has no PageLayout header (matches Figma design) so it can be
 * used in both the landing and edit route paths without nesting a second
 * role="main" landmark inside the page shell.
 */
const BrandSetupErrorPage = ({ onRetry }: Props) => (
    <Stack
        alignItems="center"
        data-testid="brand-setup-error-page"
        justifyContent="center"
        sx={{
            minHeight: 480,
            overflow: 'hidden',
            position: 'relative',
        }}
    >
        {/* Decorative blob — bottom-left (accent/radish from Figma) */}
        <Box
            aria-hidden="true"
            sx={{
                bgcolor: brandColors.radish,
                borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
                bottom: -40,
                height: 150,
                left: -60,
                position: 'absolute',
                width: 220,
            }}
        />

        {/* Decorative blob — top-right (secondary/cumin from Figma) */}
        <Box
            aria-hidden="true"
            sx={{
                bgcolor: brandColors.cumin,
                borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
                height: 210,
                position: 'absolute',
                right: -40,
                top: -50,
                width: 170,
            }}
        />

        <Stack alignItems="center" spacing={4} sx={{ position: 'relative', zIndex: 1 }}>
            <ErrorIcon aria-hidden="true" sx={{ color: 'error.main', fontSize: 80 }} />

            <Stack alignItems="center" spacing={0.5} sx={{ pt: 0.5 }}>
                <Typography
                    component="h1"
                    sx={{
                        color: 'primary.main',
                        fontSize: 24,
                        fontWeight: 500,
                        letterSpacing: '0.15px',
                        lineHeight: '32px',
                        textTransform: 'capitalize',
                    }}
                >
                    Connection Issue
                </Typography>
                <Typography
                    sx={{
                        color: 'primary.main',
                        fontSize: 16,
                        letterSpacing: '0.15px',
                        lineHeight: '24px',
                        textAlign: 'center',
                    }}
                >
                    We couldn&apos;t connect to the server. Please check your internet connection and try again.
                </Typography>
            </Stack>

            <Button
                aria-label="Try again"
                color="primary"
                data-testid="brand-setup-error-retry-button"
                onClick={onRetry}
                sx={(theme) => ({
                    borderColor: 'primary.main',
                    borderRadius: '6px',
                    borderWidth: 1.5,
                    boxShadow: `1px 1px 0px ${theme.palette.primary.main}`,
                    color: 'primary.main',
                    fontSize: 20,
                    fontWeight: 500,
                    height: 56,
                    letterSpacing: '0.5px',
                    px: 3,
                    '&:hover': {
                        borderColor: 'primary.main',
                        borderWidth: 1.5,
                        boxShadow: `1px 1px 0px ${theme.palette.primary.main}`,
                    },
                })}
                variant="outlined"
            >
                Try Again
            </Button>
        </Stack>
    </Stack>
);

export default BrandSetupErrorPage;
